<template>
  <DefaultModal
    :skip-header-underline="true"
    :footer-shadow="activeTab !== 'LOG'"
    close-btn-color="#fff"
    @close="$emit('close')"
  >
    <template #header>
      <TabHeader :tabs="tabList" @tabClick="handleTabClick" />
    </template>

    <template #body>
      <div ref="bodyWrapper" class="body-wrapper">
        <div v-if="isLoadingData" class="is-fetching-container container">
          <div class="spinner"><BaseSpinner /></div>
        </div>
        <div
          v-else-if="!isLoadingData && showError"
          class="is-fetching-container container"
        >
          <div class="error-container">
            <p>Något gick fel...</p>
            <BaseButton
              :is-rounded="true"
              type="button"
              @click.stop="fetchCurrentTab"
              >Försök igen</BaseButton
            >
          </div>
        </div>
        <template v-else>
          <template v-if="activeTab === 'GENERAL'">
            <DeleteNoteModal
              v-if="showDeleteNote"
              :note-id="noteToDeleteId"
              :application-id="applicationId"
              @close="showDeleteNote = false"
              @onDeleteSuccess="handleDeleteNoteCompleted"
            ></DeleteNoteModal>
            <EditNoteModal
              v-if="activeEditNote"
              :original-note="activeEditNote.text"
              :note-id="activeEditNote.id"
              :application-id="applicationId"
              @close="activeEditNote = null"
              @onEditSuccess="handleUpdateNote"
            ></EditNoteModal>
          </template>
          <transition name="shuffle" appear>
            <section
              v-if="activeTab === 'LOG'"
              key="notes-log"
              class="swap-log-wrapper container"
            >
              <article
                v-for="article of log"
                :key="`${article.date}-${
                  article.managerName || article.landlordName
                }-${article.type}`"
              >
                <h5>
                  {{ new Date(article.date).toLocaleDateString() }}
                  {{
                    new Date(article.date).toLocaleTimeString('sv-SE', {
                      hour: 'numeric',
                      minute: 'numeric'
                    })
                  }}
                </h5>
                <p>{{ getDescription(article) }}</p>
              </article>
            </section>
          </transition>
          <section
            v-if="activeTab === 'GENERAL' || activeTab === 'CHAT'"
            id="scroll-container"
            class="note-wrapper container"
          >
            <transition name="notes-container-fade" appear>
              <NotesContainer key="notes-list">
                <Note
                  v-for="note in notes"
                  :key="note.id"
                  :note="note"
                  :show-controls="!readOnlyMode && activeTab === 'GENERAL'"
                  :show-is-archived="false"
                  @setActiveEditNote="activeEditNote = $event"
                  @showDeleteNote="
                    showDeleteNote = true;
                    noteToDeleteId = $event;
                  "
                />
              </NotesContainer>
            </transition>
          </section>
        </template>
      </div>
    </template>

    <template #footer>
      <div class="footer-container" :class="{ 'text-options': isSkandia }">
        <div class="textarea-margin-helper">
          <transition
            :name="!readOnlyMode ? 'textarea-toggle' : ''"
            appear
            mode="in-out"
          >
            <section
              v-if="activeTab !== 'LOG' && !isSkandia"
              class="textarea-wrapper"
            >
              <textarea
                v-if="!readOnlyMode"
                class="area"
                :value="newNote"
                :placeholder="dynamicPlaceholderText"
                cols="30"
                rows="5"
                :disabled="isSaving || isLoadingData || showError"
                @input="newNote = $event.target.value"
              ></textarea>
              <p v-if="activeTab === 'CHAT'" class="info-text">
                OBS! Endast anslutna hyresvärdar får ta del av denna chatt.
              </p>
            </section>
          </transition>
          <transition
            :name="!readOnlyMode ? 'textarea-toggle' : ''"
            appear
            mode="in-out"
          >
            <section
              v-if="activeTab !== 'LOG' && isSkandia"
              class="chat-options-container"
            >
              <div class="text-info-container">
                <h3>Välj ett alternativ för att börja chatta:</h3>
                <p v-if="activeTab === 'CHAT' && isSkandia">
                  <i
                    >OBS! Endast anslutna hyresvärdar får ta del av denna
                    chatt.</i
                  >
                </p>
              </div>
              <div
                v-for="opt in skandiaChatOptionsList"
                :key="opt.id"
                class="chat-options"
              >
                <button class="chat-options-btn" @click="selectChatOption(opt)">
                  <p class="chat-option">{{ opt.text }}</p>
                </button>
              </div>
              <OptionsModal
                v-if="chatOptionModalVisible"
                :option-to-send="newNote"
                :is-chat="true"
                @send="sendChatOption"
                @close="chatOptionModalVisible = false"
              />
            </section>
          </transition>
        </div>
        <section v-if="!isSkandia" class="footer-buttons">
          <BaseButton
            v-if="!readOnlyMode && activeTab !== 'LOG'"
            :is-loading="isSaving"
            :is-rounded="true"
            :is-disabled="disabledSubmitBtn"
            :icon="svgs.ICONS.CHECK_WHITE"
            @click="handleAddNote"
            >Skicka</BaseButton
          >
          <BaseButton
            :is-disabled="isSaving"
            :is-rounded="true"
            :is-default="true"
            @click="$emit('close')"
            >Stäng</BaseButton
          >
        </section>
      </div>
    </template>
  </DefaultModal>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import statusReason from '@/utils/statusReason';
import churnService from '@/utils/churn360';
import { events, features } from '@/utils/trackingEvents';
import { getNotes, saveNote, getApplicationLog } from '@/services/appService';

import DefaultModal from '@/components/DefaultModal.vue';
import OptionsModal from '@/components/OptionsModal.vue';
import BaseButton from '@/components/BaseButton.vue';
import svgs from '@/utils/icon-constants';
import TabHeader from '@/components/TabHeader.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';
import DeleteNoteModal from '@/components/DeleteNoteModal.vue';
import EditNoteModal from '@/components/EditNoteModal.vue';
import Note from '@/components/Note.vue';
import NotesContainer from '@/components/NotesContainer.vue';

export default {
  name: 'NotesModal',
  components: {
    DefaultModal,
    OptionsModal,
    BaseButton,
    TabHeader,
    BaseSpinner,
    DeleteNoteModal,
    EditNoteModal,
    Note,
    NotesContainer
  },

  props: {
    applicationId: {
      type: String,
      default: null
    },
    unreadNotes: {
      type: Object,
      default: null
    },
    readOnlyMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['activeTabChange', 'close'],

  data() {
    return {
      svgs,
      isSaving: false,
      notes: null,
      newNote: '',
      log: [],
      activeTab: 'GENERAL',
      isLoadingData: false,
      showError: false,
      showDeleteNote: false,
      noteToDeleteId: null,
      activeEditNote: null,
      chatOptionModalVisible: false
    };
  },

  computed: {
    ...mapGetters({
      isSkandia: 'app/isSkandia'
    }),
    disabledSubmitBtn() {
      if (this.isLoadingData || this.showError) {
        return true;
      }
      if (!this.newNote || this.newNote.length < 2) {
        return true;
      }
      return false;
    },
    currentNoteType() {
      if (this.activeTab === 'CHAT') {
        return 'CHAT';
      } else if (this.activeTab === 'GENERAL') {
        return 'GENERAL';
      } else {
        return '';
      }
    },

    ...mapState('app', ['account']),
    landlordId() {
      return this.account.landlordId;
    },
    disableInternalNotes() {
      if (this.isSkandia) {
        return true;
      }
      return !this.applicationId && !this.landlordId;
    },
    disableAllNotes() {
      return !this.applicationId;
    },
    disableSwapLog() {
      return false;
    },
    tabList() {
      return [
        {
          title: 'Interna noteringar',
          active: this.activeTab === 'GENERAL',
          disabled: this.disableInternalNotes,
          hidden: this.isSkandia,
          showNotification: this.unreadNotes?.GENERAL > 0,
          tabLink: 'GENERAL'
        },
        {
          title: 'Chatt hyresvärd',
          active: this.activeTab === 'CHAT',
          disabled: this.disableAllNotes,
          showNotification: this.unreadNotes?.CHAT > 0,
          tabLink: 'CHAT'
        },
        {
          title: 'Bytets logg',
          active: this.activeTab === 'LOG',
          disabled: this.disableSwapLog,
          tabLink: 'LOG'
        }
      ];
    },
    skandiaChatOptionsList() {
      return [
        { text: 'Vi har ett gemensamt byte – kontakta mig.', id: 1 },
        {
          text: 'Vi behöver komma i kontakt med er gällande vårt gemensamma byte',
          id: 2
        }
      ];
    },
    dynamicPlaceholderText() {
      if (this.activeTab === 'CHAT') {
        return 'Skriv ett nytt meddelande här';
      } else if (this.activeTab === 'GENERAL') {
        return 'Skriv en ny notering här';
      } else return '';
    }
  },

  watch: {
    activeTab: {
      async handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          await this.fetchCurrentTab();
          this.$emit('activeTabChange', newVal);
        }
      },
      immediate: true
    }
  },

  async created() {
    if (this.isSkandia) {
      this.activeTab = 'CHAT';
    }
    await this.fetchCurrentTab();
  },

  methods: {
    getDescription({
      firstName,
      landlordName,
      managerName,
      lastName,
      text,
      type
    }) {
      const resObj = statusReason(
        {
          firstName,
          companyName: managerName || landlordName,
          lastName,
          text,
          type
        },
        true
      );
      if (resObj.name) {
        return `${resObj.name} ${resObj.text}`;
      } else {
        return resObj.text;
      }
    },
    handleUpdateNote(note) {
      const noteIndex = this.notes.findIndex(
        note => note.id === this.activeEditNote.id
      );
      if (noteIndex !== -1) {
        this.notes[noteIndex] = {
          ...this.notes[noteIndex],
          text: note,
          editedAt: true
        };
      }
      this.activeEditNote = null;
    },
    handleDeleteNoteCompleted() {
      const noteIndex = this.notes.findIndex(
        note => note.id === this.noteToDeleteId
      );
      if (noteIndex !== -1) {
        this.notes[noteIndex] = {
          ...this.notes[noteIndex],
          deletedAt: true
        };
      }
      this.showDeleteNote = false;
    },
    async fetchCurrentTab() {
      this.showError = false;
      this.isLoadingData = true;
      this.clearCurrentData();
      try {
        if (this.activeTab === 'CHAT') {
          this.notes = await getNotes(this.applicationId, ['CHAT']);
        } else if (this.activeTab === 'GENERAL') {
          this.notes = await getNotes(this.applicationId, ['GENERAL']);
        } else if (this.activeTab === 'LOG') {
          this.log = await getApplicationLog(this.applicationId);
        }
      } catch {
        this.showError = true;
      }
      this.isLoadingData = false;

      this.scrollToLatestNote();
    },
    async handleAddNote() {
      this.isSaving = true;

      const data = await saveNote(
        this.applicationId,
        this.currentNoteType,
        this.newNote
      );

      // No need to await tracking
      if (this.activeTab === 'GENERAL') {
        churnService.track(events.SENT_MESSAGE, features.SENT_MESSAGE.GENERAL);
      }
      if (this.activeTab === 'CHAT') {
        churnService.track(events.SENT_MESSAGE, features.SENT_MESSAGE.CHAT);
      }

      this.newNote = '';
      this.notes.push(data);
      this.isSaving = false;

      this.scrollToLatestNote();
    },
    async selectChatOption(opt) {
      this.newNote = opt.text;
      this.chatOptionModalVisible = true;
    },
    async sendChatOption() {
      await this.handleAddNote();
      this.chatOptionModalVisible = false;
    },
    handleTabClick(tabId) {
      this.activeTab = tabId;
    },
    clearCurrentData() {
      this.notes = null;
      this.log = [];
      this.newNote = '';
    },
    scrollToLatestNote() {
      this.$nextTick(() => {
        const scrollWrapper = this.$refs.bodyWrapper?.parentElement;
        if (scrollWrapper) {
          scrollWrapper.scrollTo(0, scrollWrapper.scrollHeight);
        }
      });
    }
  }
};
</script>

<style scoped>
.textarea-margin-helper {
  padding-top: 2.5rem;
}

.note-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.info-text {
  position: absolute;
}

.container {
  width: 44.8rem;
  max-width: 100%;
  margin: 0 auto;
  height: 40vh;
}

.swap-log-wrapper article {
  margin-bottom: 0.8rem;
  font-family: var(--font-inter);
}
.swap-log-wrapper article:nth-last-child(1) {
  margin-bottom: 0;
}
.swap-log-wrapper h5,
.swap-log-wrapper p {
  margin: 0;
  font-weight: 400;
  color: var(--color-dark-blue);
}
.swap-log-wrapper h5 {
  font-size: 1.2rem;
}
.swap-log-wrapper p {
  font-size: 1.2rem;
}

.mr-10 {
  margin-right: 10px;
}

.footer-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.chat-options-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.chat-options-container .text-info-container {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.chat-options-container .chat-options-btn {
  border: none;
  outline: none;
  padding: 0;
  cursor: pointer;
  background-color: transparent;
  box-shadow: 0px 2px 15px rgba(0, 0, 0, 0.05);
  border-radius: 1rem;
  min-width: 100%;
  max-width: 100%;
}
.chat-options-container .chat-options-btn .chat-option {
  font-size: 1.2rem;
  background-color: #fff;
  color: var(--color-grey);
  padding: 1.2rem;
  transition: transform 0.2s ease;
  border-radius: 1rem;
  border: 0.2rem solid var(--color-light-grey);
}
.chat-options-container .chat-options-btn .chat-option:hover {
  transform: scale(1.05);
  color: var(--color-dark-blue);
}

.footer-buttons {
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  gap: 15px;
}

.body-wrapper {
  position: relative;
}

.is-fetching-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  height: 5rem;
  width: 5rem;
}

#spinner {
  border-top-color: var(--color-blue);
}

.error-container {
  text-align: center;
}
.error-container p {
  font-size: 1.6rem;
  margin-bottom: 1.5rem;
}
.error-container button {
  font-size: 1.8rem;
}
</style>
